import React from 'react';
import { IS_EUROPE, BRAND, COPYRIGHT_EMAIL } from '../config/region';
import { t } from '../i18n';
import LegalPage from './LegalPage';

/* The Russian page describes the procedure under Russian copyright law and predates Plotex; it is
   kept verbatim for the ainovel.ru build. */
const RussianCopyrightPage = () => (
  <LegalPage title="Правообладателям">
    <p>Платформа AI Novel является информационным посредником, предоставляющим пользователям (авторам) возможность для размещения новелл и иного пользовательского контента. Администратор платформы не осуществляет предварительный контроль и модерацию всего размещаемого контента, но стремится оперативно реагировать на обоснованные обращения правообладателей.</p>

    <p>Для рассмотрения обращения оно должно содержать все следующие сведения:</p>
    <ol>
      <li><strong>Описание охраняемого объекта</strong> – подробное описание объекта интеллектуальной собственности, права на который, по мнению заявителя, нарушены. Описание должно быть достаточным для идентификации объекта.</li>
      <li><strong>Доказательства прав</strong> – документы и/или иные сведения, подтверждающие наличие у заявителя исключительных прав на указанный объект.</li>
      <li><strong>Точная ссылка на контент</strong> – URL-адрес страницы на платформе, на которой размещен контент, нарушающий права заявителя. Обращения без указания конкретной ссылки не рассматриваются.</li>
      <li><strong>Контактные данные</strong> – фамилия, имя, отчество заявителя (наименование организации), адрес электронной почты и телефон для связи.</li>
      <li><strong>Заявление о добросовестности</strong> – указание на то, что сведения, содержащиеся в обращении, являются достоверными, а заявитель действует добросовестно и имеет надлежащие полномочия.</li>
    </ol>

    <p>Обращения направляются на адрес электронной почты <a href={`mailto:${COPYRIGHT_EMAIL}`}>{COPYRIGHT_EMAIL}</a>.</p>
    <p>Срок первичного рассмотрения обращения — 1–3 рабочих дня с момента получения. По результатам рассмотрения Администратор вправе удалить или ограничить доступ к спорному контенту, а также заблокировать аккаунт разместившего его пользователя.</p>
    <p>Автор, чей контент был удалён, вправе направить встречное обращение по тому же адресу, приложив обоснование правомерности размещения. При получении такого обращения Администратор вправе восстановить доступ к контенту.</p>
  </LegalPage>
);

/*
 * Notice-and-takedown (DMCA / DSA).
 *
 * This page is what keeps the platform's intermediary status intact: it states that we host rather
 * than publish, gives a rightsholder a documented route to a takedown, and gives the author whose
 * work was removed a counter-notice. The elements a notice must contain are listed because a
 * notice missing them cannot be acted on.
 *
 * Notices arrive by email. The instruction only recommends a web form here ("Рекомендуется"); what
 * it makes mandatory is the page content above, which is what preserves intermediary status.
 */
const PlotexCopyrightPage = () => (
    <LegalPage
      title={t('Правообладателям')}
      description={t('Процедура уведомления о нарушении авторских прав (notice-and-takedown) на платформе {0}: требования к жалобе, порядок подачи, сроки рассмотрения и встречное уведомление.', BRAND)}
    >
      <h2>{t('Статус платформы')}</h2>
      <p>
        {t('{0} — хостинг-провайдер в значении статьи 6 Регламента (ЕС) 2022/2065 о цифровых услугах (DSA) и interactive computer service в значении раздела 230 Communications Decency Act (США). Платформа предоставляет пользователям возможность размещать собственные произведения и не является издателем пользовательского контента.', BRAND)}
      </p>
      <p>
        {t('Мы не осуществляем предварительную модерацию всего размещаемого контента, но оперативно реагируем на обоснованные уведомления правообладателей в порядке, описанном ниже. Ответственность за размещённый материал несёт создавший его пользователь.')}
      </p>

      <h2>{t('Требования к уведомлению')}</h2>
      <p>{t('Чтобы уведомление могло быть рассмотрено, оно должно содержать все перечисленные сведения:')}</p>
      <ol>
        <li>{t('Описание охраняемого произведения, достаточное для его идентификации.')}</li>
        <li>{t('Точный URL материала на платформе, который, по мнению заявителя, нарушает права. Уведомления без конкретной ссылки не рассматриваются.')}</li>
        <li>{t('Контактные данные заявителя: ФИО или наименование, адрес электронной почты, телефон.')}</li>
        <li>{t('Подтверждение полномочий — являетесь ли вы правообладателем или действуете от его имени.')}</li>
        <li>{t('Заявление о добросовестности: сведения достоверны, а использование материала не разрешено правообладателем, его агентом или законом.')}</li>
      </ol>

      <h2>{t('Сроки рассмотрения')}</h2>
      <p>
        {t('Первичный ответ на уведомление направляется в течение 1–3 рабочих дней с момента получения. По результатам рассмотрения мы вправе удалить материал или ограничить к нему доступ, а при повторных нарушениях — заблокировать аккаунт разместившего его пользователя.')}
      </p>
      <p>
        {t('Автор удалённого материала уведомляется об удалении и о причине, за исключением случаев, когда уведомление запрещено законом или требованием уполномоченного органа.')}
      </p>

      <h2>{t('Встречное уведомление')}</h2>
      <p>
        {t('Если ваш материал был удалён, а вы считаете удаление ошибочным, вы вправе направить встречное уведомление (counter-notice) на тот же адрес. Оно должно содержать: ваши контактные данные, URL удалённого материала, обоснование правомерности его размещения и заявление о добросовестности.')}
      </p>
      <p>
        {t('При получении встречного уведомления мы передаём его заявителю первоначальной жалобы. Если в разумный срок он не сообщит о начале судебного разбирательства, доступ к материалу может быть восстановлен.')}
      </p>
      <p>
        {t('Заведомо недостоверное уведомление или встречное уведомление может повлечь ответственность за причинённые убытки.')}
      </p>

      <h2>{t('Как подать уведомление')}</h2>
      <p>
        {t('Направьте уведомление на адрес {0}, включив в письмо все сведения, перечисленные в разделе «Требования к уведомлению» выше. Уведомление, в котором чего-то из них не хватает, рассмотреть невозможно.', COPYRIGHT_EMAIL)}
      </p>
      <p>
        <a className="lp-mail" href={`mailto:${COPYRIGHT_EMAIL}?subject=${encodeURIComponent(t('Уведомление о нарушении авторских прав — {0}', BRAND))}`}>
          {t('Написать на {0}', COPYRIGHT_EMAIL)}
        </a>
      </p>
  </LegalPage>
);

const CopyrightPage = () => (IS_EUROPE ? <PlotexCopyrightPage /> : <RussianCopyrightPage />);

export default CopyrightPage;
